import { Component, h, Prop } from '@stencil/core';

/**
 * How-to decoration component that displays step-by-step illustrations for signing documents.
 * Renders different image layouts based on the mediaId prop (screen01 through screen06).
 *
 * @product signnow
 * @category Product
 * @example
 *   <sn-how-to-decoration mediaId="screen01" />
 */
@Component({
  tag: 'sn-how-to-decoration',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHowToDecoration {
  /**
   * The media ID to display (e.g. 'screen01', 'screen02', etc.)
   */
  @Prop() mediaId: string;

  private renderScreen01() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen01" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1@2x.webp 2x"
            alt="How to Sign a PDF Online"
            aspectRatio="525/350"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2@2x.webp 2x"
            alt="How to Sign a PDF Online"
            aspectRatio="320/220"
          />
        </div>
      </div>
    );
  }

  private renderScreen02() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen02" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1@2x.webp 2x"
            alt="How to Sign a PDF Using Google Chrome"
            aspectRatio="525/350"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2@2x.webp 2x"
            alt="How to Sign a PDF Using Google Chrome"
            aspectRatio="255/212"
          />
        </div>
      </div>
    );
  }

  private renderScreen03() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen03" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1@2x.webp 2x"
            alt="How to Sign a PDF in Gmail"
            aspectRatio="525/350"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2@2x.webp 2x"
            alt="How to Sign a PDF in Gmail"
            aspectRatio="1/1"
          />
          <me-picture
            class="sign-illustration__button"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3@2x.webp 2x"
            alt="How to Sign a PDF in Gmail"
            aspectRatio="156/54"
          />
        </div>
      </div>
    );
  }

  private renderScreen04() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen04" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1@2x.webp 2x"
            alt="How to Sign a PDF on a Mobile Device"
            aspectRatio="210/442"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2@2x.webp 2x"
            alt="How to Sign a PDF on a Mobile Device"
            aspectRatio="164/220"
          />
          <me-picture
            class="sign-illustration__image-tertiary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3@2x.webp 2x"
            alt="How to Sign a PDF on a Mobile Device"
            aspectRatio="164/220"
          />
        </div>
      </div>
    );
  }

  private renderScreen05() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen05" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1@2x.webp 2x"
            alt="How to Sign a PDF on iPhone"
            aspectRatio="262/427"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-2.png 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-05-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-05-2.webp 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-05-2@2x.webp 2x"
            alt="How to Sign a PDF on iPhone"
            aspectRatio="262/427"
          />
        </div>
      </div>
    );
  }

  private renderScreen06() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen06" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1.png"
            srcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1.png 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1.webp 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1@2x.webp 2x"
            alt="How to Sign a PDF on Android"
            aspectRatio="261/460"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2.png"
            srcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2.png 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2.webp 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2@2x.webp 2x"
            alt="How to Sign a PDF on Android"
            aspectRatio="261/460"
          />
        </div>
      </div>
    );
  }

  render() {
    switch (this.mediaId) {
      case 'screen01':
        return this.renderScreen01();
      case 'screen02':
        return this.renderScreen02();
      case 'screen03':
        return this.renderScreen03();
      case 'screen04':
        return this.renderScreen04();
      case 'screen05':
        return this.renderScreen05();
      case 'screen06':
        return this.renderScreen06();
      default:
        return null;
    }
  }
}
